import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Switch, Checkbox, Button, Modal, message } from "antd";
import PropTypes from "prop-types";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { EditOutlined } from "@mui/icons-material";
import circleWarning from "../../src/assets/icons/Circle_Warning.svg";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { useSelector } from "react-redux";
import { getClientid } from "redux/selectors";

const Container = styled.div`
  position: relative;
  height: calc(100vh - 450px);

  width: 100%;
  font-family: Inter, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  color: #222;

  .note {
    font-size: 13px;
    color: #666;
    margin-bottom: 12px;
  }

  .card {
    border: 1px solid #e6e6e6;
    border-radius: 10px;
    padding: 18px;
    background: #fff;
  }

  .table-wrap {
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid #e9ecef;
    background: white;
    -webkit-overflow-scrolling: touch;
  }

  /* permission table */
  .permission-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 900px;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 3;
    background: #fff;
  }

  thead th {
    padding: 14px 16px;
    border-bottom: 1px solid #eef1f3;
    font-weight: 600;
    font-size: 15px;
    text-align: left;
  }

  thead tr.second-row th {
    background: #fafbfd;
    font-weight: 500;
    font-size: 14px;
  }

  /* cells */
  tbody td {
    padding: 12px 16px;
    border-top: 1px solid #f3f4f6;
    font-size: 14px;
    white-space: nowrap;
    vertical-align: middle;
  }

  tbody tr:nth-of-type(odd) td {
    background: #fafafa;
  }

  /* First column: menu name */
  .permission-table th:first-child,
  .menu-cell {
    text-align: left;
  }

  .menu-cell {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    min-width: 260px;
  }

  /* group row */
  .group-row td {
    padding: 10px 18px;
    background: #fff;
    border-top: 0;
    font-weight: 700;
  }

  .group-row-cell {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    width: 75%;
  }

  /* center permission switches */
  .permission-cell,
  .center {
    text-align: center;
    vertical-align: middle;
    width: 120px; /* fixed width per permission column to align switches */
  }

  .switch-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  /* ant switch sizing tweak */
  .ant-switch {
    min-width: 34px;
    height: 18px;
    border-radius: 12px !important;
  }
  .ant-switch-handle {
    width: 14px;
    height: 14px;
  }

  .ant-switch-checked {
    background-color: #03c83e !important;
  }
  .ant-switch:not(.ant-switch-checked) {
    background-color: #d9d9d9 !important;
  }

  .compact-list {
    margin-top: 14px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #f0f2f5;
  }

  .compact-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-top: 1px solid #f3f4f6;
    background: #fff;
    cursor: pointer;
  }

  .compact-row:first-of-type {
    border-top: 0;
  }

  .compact-row:hover {
    background: #fafbfd;
  }

  .footer-actions {
    margin-top: 12px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .subtitle {
    font-size: 12px;
    color: #666;
  }
`;

const StyledButton = styled(Button)`
  color: white;
  width:80px;
  background-color: #00685F;
  border-radius: 5px;
  border-color: #00685F;
  margin-left: 5%;
  color: #ffffff !important;
  &:hover {   background-color: #00685F;
  border-color: #00685F;
  color: #ffffff !important;
  
  }

`;

const RolesAndPermissions = ({
  menuList = [],
  setMenuList,
  saveMenuList,
  MenuList,
  selectedRoleId,
  viewOnly = false,
  paydisabled = false,
  step,
  setStep,
  onFinish,
  loading,
  type = "",
  handleCancel = () => { },
  isModalVisible,
  setIsView,
  setIsEditPermission,
  permissionForEdit
}) => {
  const deepClone = (o) => JSON.parse(JSON.stringify(o));
  const [local, setLocal] = useState(deepClone(menuList || []));
  const [expandedItems, setExpandedItems] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [applyScope, setApplyScope] = useState("upcomingEmployees");
  const [apiLoading, setApiLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [effectivePermissionForEdit, setEffectivePermissionForEdit] = useState(permissionForEdit);
  useEffect(() => setEffectivePermissionForEdit(permissionForEdit), [permissionForEdit]);
  useEffect(() => setLocal(deepClone(menuList || [])), [menuList]);
  const clientId = useSelector(getClientid);

  useEffect(() => {
    const ids = (local || [])
      .filter((i) => i.subMenu && i.subMenu.length > 0 && !!i.permission?.view)
      .map((i) => i.id);
    setExpandedItems(ids);
  }, [local]);

  const emitChange = (updated) => {
    if (typeof setMenuList === "function") {
      try {
        setMenuList(updated);
      } catch {
        try {
          setMenuList(() => updated);
        } catch { }
      }
    }
    if (Array.isArray(MenuList) && typeof saveMenuList === "function") {
      const cloned = deepClone(MenuList);
      if (typeof selectedRoleId === "number") cloned[selectedRoleId] = deepClone(updated);
      saveMenuList(cloned);
    }
  };


  const handleSave = () => {
    if (type === "add") {
      handleConfirmOk();
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmOk = async () => {
    console.log(permissionForEdit, "permissionForEdit")
    if (permissionForEdit) {
      try {
        setApiLoading(true);
        const response = await CALL_FEATCH_API(`roles-and-permissions/${clientId}?path=updateDefaultPermission`, "patch",
          {
            roleId: selectedRoleId,
            menuList: local,
            defaultRolePermission: applyScope,
          }
        );
        if (response?.status === "SUCCESS") {
          message.success("Permissions updated successfully!");
          if (typeof onFinish === "function") onFinish(local);
        } else {
          message.error("Failed to update permissions");
        }
      } catch (err) {
        message.error("Something went wrong");
      } finally {
        setApiLoading(false);
      }
    } else {
      if (typeof setMenuList === "function")
        setMenuList(local);
      if (Array.isArray(MenuList) && typeof saveMenuList === "function") {
        const cloned = deepClone(MenuList);
        if (selectedRoleId != null) cloned[selectedRoleId] = deepClone(local);
        saveMenuList(cloned);
      }
      if (typeof onFinish === "function") onFinish(local);
    }
    setShowConfirm(false);
  };

  const handleConfirmCancel = () => {
    message.info("Action cancelled");
    setShowConfirm(false);
  };

  const toggleSystem = (systemKey, checked, groupItems = [], systemChecked) => {
    const idsToToggle = (groupItems || []).map((g) => g.id);

    setLocal((prev) => {
      const updated = prev.map((it) => {
        if (!idsToToggle.includes(it.id)) return it;

        const newItem = { ...it };

        if (checked) {
          newItem.access = true;
          newItem.permission = {
            view: true,
            create: true,
            edit: true,
            delete: true,
          };
          newItem.subMenu = (newItem.subMenu || []).map((s) => ({
            ...s,
            permission: {
              view: true,
              create: true,
              edit: true,
              delete: true,
            },
            access: true,
          }));
        } else {
          newItem.access = false;
          newItem.permission = {
            view: false,
            create: false,
            edit: false,
            delete: false,
          };
          newItem.subMenu = (newItem.subMenu || []).map((s) => ({
            ...s,
            permission: {
              view: false,
              create: false,
              edit: false,
              delete: false,
            },
            access: false,
          }));
        }

        return newItem;
      });

      emitChange(updated);
      return updated;
    });

    setExpandedItems((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...idsToToggle]));
      }
      return prev.filter((id) => !idsToToggle.includes(id));
    });
  };

  const updatePermission = (roleId, permissionType, submenuKey) => {
    setLocal((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== roleId) return item;

        if (!submenuKey) {
          const newPerm = { ...(item.permission || {}) };
          if (permissionType === "view") {
            newPerm.view = !newPerm.view;
            if (!newPerm.view) {
              newPerm.create = false;
              newPerm.edit = false;
              newPerm.delete = false;
            }
          } else {
            newPerm[permissionType] = !newPerm[permissionType];
            if (newPerm.create || newPerm.edit || newPerm.delete) newPerm.view = true;
          }
          const newSubMenu = (item.subMenu || []).map((s) => {
            if (!newPerm.view)
              return {
                ...s,
                permission: { create: false, view: false, edit: false, delete: false },
                access: false,
              };
            return s;
          });
          const parentAccess = newSubMenu.length ? newSubMenu.some((s) => s.access) : !!newPerm.view;
          return { ...item, permission: newPerm, subMenu: newSubMenu, access: parentAccess };
        }

        const newSubMenu = (item.subMenu || []).map((s) => {
          if (s.key !== submenuKey) return s;
          const newPerm = { ...(s.permission || {}) };
          if (permissionType === "view") {
            newPerm.view = !newPerm.view;
            if (!newPerm.view) {
              newPerm.create = false;
              newPerm.edit = false;
              newPerm.delete = false;
            }
          } else {
            newPerm[permissionType] = !newPerm[permissionType];
            if (newPerm.create || newPerm.edit || newPerm.delete) newPerm.view = true;
          }
          return { ...s, permission: newPerm, access: newPerm.view || newPerm.create || newPerm.edit || newPerm.delete };
        });
        const parentAccess = newSubMenu.some((s) => s.access);
        return { ...item, subMenu: newSubMenu, access: parentAccess };
      });

      emitChange(updated);
      return updated;
    });
  };

  /* grouping logic unchanged */
  const groups = {};
  const order = [];
  local.forEach((item) => {
    const hasSub = item.subMenu && item.subMenu.length > 0;
    const key = hasSub ? item.menuName?.trim() || item.title || "Group" : "Home";
    if (!groups[key]) {
      groups[key] = [];
      order.push(key);
    }
    groups[key].push(item);
  });
  const orderedKeys = [...new Set(["Home", ...order.filter((k) => k !== "Home")])];
  const defaultExpandedArray = orderedKeys || [];
  const [expandedGroup, setExpandedGroup] = useState(defaultExpandedArray);

  useEffect(() => {
    const newDefault = orderedKeys.length ? orderedKeys : [];
    setExpandedGroup(newDefault);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuList]);

  const footerStyle = {
    position: "sticky",
    bottom: -10,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    padding: "10px",
    display: "flex",
    justifyContent: "end",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    margin: "0px -20px",
  };

  const handleEditPermission = () => {
    setIsView(false);
    setIsEditPermission(true);
  };

  return (
    <Container>
      {type === "add" && (
        <div className="note">
          Note: You can specify default permissions when creating a role, and these can be customized for individual employees under respective role if needed.
        </div>
      )}

      <div className="card">
        {viewOnly && !isEditing && (
          <>
            {local?.length > 0 && (
              <div
                style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}
                className="d-flex align-items-center"
              >
                <ButtonFilled
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 6,
                  }}
                  // type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    if (isModalVisible) {
                      setIsEditing(true);
                      setEffectivePermissionForEdit(true);
                    } else {
                      handleEditPermission();
                    }
                  }}
                >
                  <span>Edit</span>
                </ButtonFilled>
              </div>
            )}
          </>
        )}

        <div className="table-wrap">
          {local?.length > 0 ? (
            <table className="permission-table">
              <thead>
                <tr>
                  <th rowSpan="2">Menu Access Level</th>
                  <th colSpan="4" className="text-center">Permissions</th>
                </tr>
                <tr className="second-row">
                  <th>View</th>
                  <th>Create</th>
                  <th>Modify</th>
                  <th>Delete</th>
                </tr>
              </thead>

              <tbody>
                {expandedGroup
                  .filter((groupKey) => orderedKeys.includes(groupKey))
                  .map((groupKey) => {
                    const groupItems = groups[groupKey] || [];
                    const systemKey = groupItems[0]?.system || groupKey;
                    const systemChecked = groupItems.some((g) => g.access);

                    return (
                      <React.Fragment key={groupKey}>
                        <tr className="group-row">
                          <td>
                            <div className="group-row-cell">
                              <div style={{ fontWeight: 700 }}>{groupKey}</div>

                              <Switch
                                checked={!!systemChecked}
                                disabled={viewOnly && !isEditing}
                                onChange={(checked) =>
                                  toggleSystem(systemKey, checked, groupItems, systemChecked)
                                }
                              />
                            </div>
                          </td>
                        </tr>

                        {systemChecked &&
                          groupItems.map((item) => {
                            if (item.subMenu && item.subMenu.length > 0) {
                              return item.subMenu.map((sub) => (
                                <tr key={`${item.id}-${sub.key}`}>
                                  <td className="menu-cell">
                                    <div>{sub?.title}</div>
                                  </td>

                                  <td className="permission-cell">
                                    <div className="switch-wrapper">
                                      <Switch size="small" checked={!!sub.permission?.view} disabled={viewOnly && !isEditing} onChange={() => updatePermission(item.id, "view", sub.key)} />
                                    </div>
                                  </td>

                                  <td className="permission-cell">
                                    <div className="switch-wrapper">
                                      <Switch size="small" checked={!!sub.permission?.create} disabled={(viewOnly && !isEditing) || !sub.permission?.view} onChange={() => updatePermission(item.id, "create", sub.key)} />
                                    </div>
                                  </td>

                                  <td className="permission-cell">
                                    <div className="switch-wrapper">
                                      <Switch size="small" checked={!!sub.permission?.edit} disabled={(viewOnly && !isEditing) || !sub.permission?.view} onChange={() => updatePermission(item.id, "edit", sub.key)} />
                                    </div>
                                  </td>

                                  <td className="permission-cell">
                                    <div className="switch-wrapper">
                                      <Switch size="small" checked={!!sub.permission?.delete} disabled={(viewOnly && !isEditing) || !sub.permission?.view} onChange={() => updatePermission(item.id, "delete", sub.key)} />
                                    </div>
                                  </td>
                                </tr>
                              ));
                            }

                            return (
                              <tr key={`single-${item.id}`}>
                                <td className="menu-cell">
                                  <div>{item.title}</div>
                                  {(item?.title === "Dashboard" || item?.title === "Calendar") && <div className="subtitle">{item?.subtitle}</div>}
                                </td>

                                <td className="permission-cell">
                                  <div className="switch-wrapper">
                                    <Switch size="small" checked={!!item.permission?.view} disabled={viewOnly && !isEditing} onChange={() => updatePermission(item.id, "view")} />
                                  </div>
                                </td>

                                <td className="permission-cell">
                                  <div className="switch-wrapper">
                                    <Switch size="small" checked={!!item.permission?.create} disabled={(viewOnly && !isEditing) || !item.permission?.view} onChange={() => updatePermission(item.id, "create")} />
                                  </div>
                                </td>

                                <td className="permission-cell">
                                  <div className="switch-wrapper">
                                    <Switch size="small" checked={!!item.permission?.edit} disabled={(viewOnly && !isEditing) || !item.permission?.view} onChange={() => updatePermission(item.id, "edit")} />
                                  </div>
                                </td>

                                <td className="permission-cell">
                                  <div className="switch-wrapper">
                                    <Switch size="small" checked={!!item.permission?.delete} disabled={(viewOnly && !isEditing) || !item.permission?.view} onChange={() => updatePermission(item.id, "delete")} />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          ) : (
            <div>
              <div style={{ textAlign: "center", padding: 20 }}>
                <p>No data available</p>
              </div>
            </div>
          )}
        </div>

        <div className="compact-list" style={{ marginTop: 16 }}>
          {orderedKeys.map((k) => {
            if (expandedGroup.includes(k)) return null;
            const group = groups[k] || [];
            if (group.length === 0) return null;
            const systemKey = group[0]?.system || k;
            const systemChecked = group.some((g) => g.access);
            return (
              <div
                key={`compact-${k}`}
                className="compact-row"
                onClick={() =>
                  setExpandedGroup((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]))
                }
              >
                <div style={{ fontWeight: 600 }}>{k}</div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={!!systemChecked}
                    disabled={viewOnly && !isEditing}
                    onChange={(checked) => {
                      toggleSystem(systemKey, checked, group, systemChecked);
                    }}
                  />
                </div>
              </div>
            );
          })}
          {(type !== "view" && !viewOnly) || isEditing ? (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "10px" }}>
              <>
                <ButtonOutlined onClick={() => {
                  if (isEditing) {
                    setIsEditing(false);
                    setLocal(deepClone(menuList || []));
                  } else {
                    handleCancel(false);
                  }
                }}>Cancel</ButtonOutlined>
                {type === "add" && <ButtonOutlined onClick={() => setStep && setStep(step - 1)}>Previous</ButtonOutlined>}
                <ButtonFilled loading={loading || apiLoading} disabled={local?.length === 0} onClick={handleSave}>
                  {type === "add" ? "Create" : "Save Changes"}
                </ButtonFilled>
              </>
            </div>
          ) : null}
        </div>
      </div>


      <Modal
        open={showConfirm}
        onCancel={handleConfirmCancel}
        centered
        footer={null}
        bodyStyle={{ borderRadius: "12px" }}
        style={{ borderRadius: "12px" }}
      >
        {effectivePermissionForEdit ? (
          <>
            <div className="d-flex justify-content-center mb-3">
              <img src={circleWarning} alt="Warning" />
            </div>
            <p style={{ marginBottom: 16, fontWeight: 500 }}>
              You want to apply the changes for
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input type="radio" name="applyScope" value="upcoming"
                  checked={applyScope === "upcomingEmployees"}
                  onChange={(e) => setApplyScope(e.target.value)} />
                Upcoming employees
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input type="radio" name="applyScope" value="existingWithoutCustom"
                  checked={applyScope === "existingWithoutCustom"}
                  onChange={(e) => setApplyScope(e.target.value)} />
                All existing employees without custom permissions
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input type="radio" name="applyScope" value="existingWithCustom"
                  checked={applyScope === "existingWithCustom"}
                  onChange={(e) => setApplyScope(e.target.value)} />
                All existing employees and including those with custom permissions
              </label>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px",alignItems:"center" }}>
              <Button onClick={handleConfirmCancel}
                style={{ border: "1px solid #000", color: "#000", background: "#fff", borderRadius: "8px", width: "80px" }}>
                Cancel
              </Button>
              <ButtonFilled loading={apiLoading} onClick={handleConfirmOk}
                style={{ background: "#00685F", borderColor: "#00685F", borderRadius: "8px" }}>
                Save Changes
              </ButtonFilled>
            </div>
          </>
        ) : (
          <>
            <div className="d-flex justify-content-center">
              <img src={circleWarning} alt="Warning" />
            </div>
            <p style={{ marginBottom: 24 }}>
              You are customizing permissions specifically for this employee. <br />
              This will override the default role-based permissions.
              <br />
              Do you want to continue with the changes?
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <Button onClick={handleConfirmCancel}
                style={{ border: "1px solid #000", color: "#000", background: "#fff", borderRadius: "8px", width: "80px" }}>
                No
              </Button>
              <StyledButton loading={loading} onClick={handleConfirmOk}
              // style={{ background: "#3DD598", borderColor: "#3DD598", borderRadius: "8px", width: "80px" }}
              >
                Yes
              </StyledButton>
            </div>
          </>
        )}
      </Modal>
    </Container>
  );
};

RolesAndPermissions.propTypes = {
  menuList: PropTypes.array,
  setMenuList: PropTypes.func,
  saveMenuList: PropTypes.func,
  MenuList: PropTypes.array,
  selectedRoleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  viewOnly: PropTypes.bool,
  paydisabled: PropTypes.bool,
  step: PropTypes.number,
  setStep: PropTypes.func,
};

export default RolesAndPermissions;